/**
 * Cook mode (ROADMAP §3 WS-19) — pure timer-store logic, the "single driven store" the
 * timer bar renders from. No `setInterval`/`requestAnimationFrame` lives in here: a caller
 * (the `useCookTimers` hook in `pages/CookModePage.tsx`) drives one interval/rAF loop and
 * calls `tick()` on it. Every timer stores an absolute `endTime` (epoch ms), computed once
 * at start/resume — remaining time is always `endTime - now`, so a throttled/backgrounded
 * tab (Chrome clamps `setInterval` to ~1/min in the background) can never make a timer
 * *drift*; the worst it can do is deliver the "done" event late, which `tick()` catches up
 * on the next call by returning every timer whose `endTime` has already passed.
 *
 * Honest limitation (see docblock on `CookModePage`): timers only run while this tab is
 * open or briefly backgrounded — there is no server-side scheduling, so a fully closed tab
 * or terminated PWA loses them. v1 scope only.
 */

export type CookTimerStatus = 'running' | 'paused' | 'done';

export interface CookTimer {
  id: string;
  /** e.g. "Step 3 · simmer sauce" */
  name: string;
  /** Total duration as started/resumed with, for restart/redisplay. */
  durationSeconds: number;
  status: CookTimerStatus;
  /** Absolute epoch ms this timer finishes, if running. `null` when paused/done. */
  endTime: number | null;
  /** Seconds left, frozen at the moment of pausing. `null` while running. */
  remainingAtPause: number | null;
}

export interface CookTimersState {
  timers: CookTimer[];
}

export const EMPTY_TIMERS_STATE: CookTimersState = { timers: [] };

export function startTimer(
  state: CookTimersState,
  timer: { id: string; name: string; durationSeconds: number },
  now: number,
): CookTimersState {
  const next: CookTimer = {
    id: timer.id,
    name: timer.name,
    durationSeconds: timer.durationSeconds,
    status: 'running',
    endTime: now + timer.durationSeconds * 1000,
    remainingAtPause: null,
  };
  return { timers: [...state.timers, next] };
}

function updateTimer(
  state: CookTimersState,
  id: string,
  fn: (timer: CookTimer) => CookTimer,
): CookTimersState {
  return { timers: state.timers.map((t) => (t.id === id ? fn(t) : t)) };
}

/** Remaining whole seconds for a timer at instant `now` (never negative). */
export function remainingSeconds(timer: CookTimer, now: number): number {
  if (timer.status === 'paused') return Math.max(0, timer.remainingAtPause ?? 0);
  if (timer.status === 'done') return 0;
  if (timer.endTime === null) return 0;
  return Math.max(0, Math.ceil((timer.endTime - now) / 1000));
}

export function pauseTimer(state: CookTimersState, id: string, now: number): CookTimersState {
  return updateTimer(state, id, (t) => {
    if (t.status !== 'running') return t;
    return {
      ...t,
      status: 'paused',
      remainingAtPause: remainingSeconds(t, now),
      endTime: null,
    };
  });
}

export function resumeTimer(state: CookTimersState, id: string, now: number): CookTimersState {
  return updateTimer(state, id, (t) => {
    if (t.status !== 'paused') return t;
    return {
      ...t,
      status: 'running',
      endTime: now + (t.remainingAtPause ?? 0) * 1000,
      remainingAtPause: null,
    };
  });
}

export function cancelTimer(state: CookTimersState, id: string): CookTimersState {
  return { timers: state.timers.filter((t) => t.id !== id) };
}

/**
 * Advances the store to instant `now`. Any `running` timer whose `endTime` has passed
 * flips to `done` and is returned in `justFinished` (in the order they finish) so the
 * caller can chime/notify/flash exactly once per timer. Calling `tick` repeatedly with the
 * same or an earlier `now` is a no-op past the first crossing — a timer already `done`
 * never re-fires.
 */
export function tick(
  state: CookTimersState,
  now: number,
): { state: CookTimersState; justFinished: CookTimer[] } {
  const justFinished: CookTimer[] = [];
  const timers = state.timers.map((t) => {
    if (t.status === 'running' && t.endTime !== null && t.endTime <= now) {
      const finished: CookTimer = { ...t, status: 'done', endTime: null };
      justFinished.push(finished);
      return finished;
    }
    return t;
  });
  return { state: { timers }, justFinished };
}

/** `mm:ss` (or `h:mm:ss` past an hour) — the timer bar's countdown format. */
export function formatCountdown(totalSeconds: number): string {
  const s = Math.max(0, Math.round(totalSeconds));
  const hours = Math.floor(s / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const seconds = s % 60;
  const mm = String(minutes).padStart(hours > 0 ? 2 : 1, '0');
  const ss = String(seconds).padStart(2, '0');
  return hours > 0 ? `${hours}:${String(minutes).padStart(2, '0')}:${ss}` : `${mm}:${ss}`;
}
